import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, fmtBRL, fmtNum, fmtKg } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import { ArrowLeft, Printer, Pencil, CheckCircle2, XCircle, Sprout, Unlock, Truck } from "lucide-react";

const CATS = [
  { k: "cx1", label: "CX1" }, { k: "cx2", label: "CX2" },
  { k: "cx3", label: "CX3" }, { k: "cx3g", label: "CX3G" }, { k: "cx4", label: "CX4" },
];

const COMPANY = {
  name: "Cebolas Serenata Ltda",
  cnpj: "43.616.871/0002-28",
  endereco: "Rod SC 110, S/N KM 9 — Ituporanga/SC",
};

export default function ShipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const editNfSaida = async () => {
    const numero = window.prompt("Número da NF de saída (aparece no Contas a Receber):", ship.nf_saida_numero || "");
    if (numero === null) return;
    try {
      await api.patch(`/shipments/${id}/nf-saida`, { nf_saida_numero: numero });
      setShip({ ...ship, nf_saida_numero: numero.trim() });
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao salvar NF"); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/shipments/${id}`); setShip(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const finalize = async () => {
    if (!window.confirm("Finalizar esta saída? O estoque será debitado.")) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/shipments/${id}/finalize`); setShip(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const cancelShip = async () => {
    if (!window.confirm("Cancelar esta saída?")) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/shipments/${id}/cancel`); setShip(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reabrir = async () => {
    if (!window.confirm(
      "Reabrir esta saída para edição?\n\n" +
      "• O status voltará para RASCUNHO\n" +
      "• A conta a receber será cancelada (se não tiver pagamentos)\n" +
      "• Após editar, você precisará finalizar novamente"
    )) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/shipments/${id}/reabrir`);
      setShip(data);
      nav(`/shipments/${id}/edit`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Layout title="Saída"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;
  if (!ship) return <Layout title="Saída"><div className="text-danger">{err || "Não encontrada"}</div></Layout>;

  const cli = ship.cliente_snapshot || {};
  const num = String(ship.romaneio_saida_numero || 0).padStart(4, "0");

  return (
    <Layout
      title={`Romaneio RS-${num}`}
      subtitle={`${ship.cliente} • ${fmtDate(ship.created_at)}`}
      actions={
        <div className="flex gap-2 print:hidden">
          <Button variant="secondary" onClick={() => nav("/shipments")}><ArrowLeft size={16} /> Voltar</Button>
          <Button variant="secondary" onClick={() => nav(ship.shipment_status === "rascunho" ? `/expedicao?shipment=${id}` : "/expedicao")} data-testid="shipment-to-expedicao-button">
            <Truck size={16} /> {ship.shipment_status === "rascunho" ? "Editar bipagem" : "Ir p/ Expedição"}
          </Button>
          {ship.shipment_status === "rascunho" && (
            <Button variant="secondary" onClick={() => nav(`/shipments/${id}/edit`)} data-testid="shipment-edit-button"><Pencil size={16} /> Editar</Button>
          )}
          <Button variant="secondary" onClick={() => window.print()} data-testid="shipment-print-button"><Printer size={16} /> Imprimir</Button>
          {ship.shipment_status === "rascunho" && (
            <Button onClick={finalize} disabled={busy} data-testid="shipment-finalize-button">
              {busy ? <Spinner size={16} /> : <><CheckCircle2 size={16} /> Finalizar</>}
            </Button>
          )}
          {ship.shipment_status === "finalizado" && (
            <Button variant="secondary" onClick={reabrir} disabled={busy} data-testid="shipment-reopen-button">
              {busy ? <Spinner size={16} /> : <><Unlock size={16} /> Reabrir p/ edição</>}
            </Button>
          )}
          {ship.shipment_status === "finalizado" && (
            <Button variant="danger" onClick={cancelShip} disabled={busy} data-testid="shipment-cancel-button"><XCircle size={16} /> Cancelar</Button>
          )}
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4 print:hidden">{err}</div>}

      {/* Print header (visible apenas em impressão) */}
      <div className="hidden print:block mb-3">
        <div className="flex items-center justify-between border-b-2 border-stone-900 pb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-stone-900 flex items-center justify-center"><Sprout className="text-white" size={16} /></div>
            <div>
              <div className="font-heading font-extrabold text-sm leading-tight">{COMPANY.name}</div>
              <div className="text-[9px] leading-tight">CNPJ: {COMPANY.cnpj}</div>
              <div className="text-[9px] leading-tight">{COMPANY.endereco}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading font-extrabold text-lg leading-tight">ROMANEIO DE SAÍDA</div>
            <div className="text-xs font-mono">Nº RS-{num}</div>
            <div className="text-[9px]">Data: {fmtDate(ship.created_at)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:block print:gap-0">
        <div className="lg:col-span-2 space-y-6 print:space-y-2">
          {/* Cliente */}
          <Card className="p-6 print:p-0 print:border-0 print:shadow-none">
            <div className="flex items-center justify-between mb-3 print:mb-1">
              <h3 className="font-heading text-lg print:text-[11px] print:uppercase print:tracking-widest font-bold text-ink-900">Cliente</h3>
              <Badge tone={ship.shipment_status === "finalizado" ? "success" : ship.shipment_status === "cancelado" ? "danger" : "warn"} className="print:hidden">{ship.shipment_status}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm print:text-[10px] print:gap-x-3 print:gap-y-0.5">
              <Field label="Razão social" value={cli.nome || ship.cliente} />
              <Field label="CNPJ/CPF" value={cli.cnpj_cpf} mono />
              <Field label="IE" value={cli.ie} mono />
              <Field label="Endereço" value={[cli.logradouro, cli.numero, cli.bairro].filter(Boolean).join(", ")} />
              <Field label="Cidade/UF" value={[cli.cidade, cli.uf].filter(Boolean).join("/")} />
              <Field label="CEP" value={cli.cep} mono />
              <Field label="Destino" value={ship.destino} />
              <Field label="Motorista" value={ship.motorista} />
              <Field label="Placa" value={ship.placa} mono />
              <div className="print:hidden" data-testid="shipment-detail-nf-saida">
                <Field label="Nº NF de saída" value={ship.nf_saida_numero || "—"} mono />
                <button type="button" onClick={editNfSaida} className="text-xs font-semibold text-brand-700 hover:underline mt-1" data-testid="shipment-nf-saida-edit">
                  {ship.nf_saida_numero ? "Alterar NF" : "Informar NF"}
                </button>
              </div>
              {ship.vendedor && <Field label="Vendedor" value={ship.vendedor} />}
            </div>
          </Card>

          {/* Itens */}
          <Card className="overflow-hidden print:border print:shadow-none">
            <div className="px-6 py-4 print:px-2 print:py-1 border-b border-ink-200">
              <h3 className="font-heading text-lg print:text-[11px] font-bold text-ink-900 uppercase tracking-wider">Itens</h3>
            </div>
            <table className="w-full text-sm print:text-[10px]">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-xs print:text-[9px] font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-3 py-2 print:px-1.5 print:py-0.5 text-left">Tipo</th>
                  {CATS.map((c) => <th key={c.k} className="px-3 py-2 print:px-1.5 print:py-0.5 text-right">{c.label}</th>)}
                  <th className="px-3 py-2 print:px-1.5 print:py-0.5 text-right">Total/scs</th>
                  <th className="px-3 py-2 print:px-1.5 print:py-0.5 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Agrupa itens por tipo_cebola: soma quantidades e valores
                  const grouped = {};
                  for (const it of (ship.itens_calc || [])) {
                    const tipo = (it.tipo_cebola || "amarela").toLowerCase();
                    if (!grouped[tipo]) {
                      grouped[tipo] = { tipo_cebola: tipo, total_caixas: 0, valor: 0 };
                      for (const c of CATS) grouped[tipo][c.k] = { quantidade: 0, preco: 0 };
                    }
                    const g = grouped[tipo];
                    for (const c of CATS) {
                      const cell = it[c.k] || {};
                      g[c.k].quantidade += Number(cell.quantidade || 0);
                      // Preço: pega o primeiro >0 (deve ser mesmo para o mesmo tipo/categoria)
                      if (!g[c.k].preco && cell.preco) g[c.k].preco = Number(cell.preco);
                    }
                    g.total_caixas += Number(it.total_caixas || 0);
                    g.valor += Number(it.valor || 0);
                  }
                  return Object.values(grouped).map((it, idx) => (
                    <tr key={idx} className="border-b border-ink-200 last:border-0">
                      <td className="px-3 py-2 print:px-1.5 print:py-0.5 capitalize">{it.tipo_cebola}</td>
                      {CATS.map((c) => {
                        const cell = it[c.k] || {};
                        const q = cell.quantidade || 0;
                        const p = cell.preco || 0;
                        return (
                          <td key={c.k} className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right">
                            <div>{fmtNum(q, 0)}</div>
                            {q > 0 && p > 0 && (
                              <div className="text-[10px] print:text-[8px] text-ink-500 leading-tight">{fmtBRL(p)}</div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right font-semibold">{fmtNum(it.total_caixas, 0)}</td>
                      <td className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right">{fmtBRL(it.valor)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
              <tfoot className="bg-stone-50">
                <tr className="font-bold">
                  <td className="px-3 py-2 print:px-1.5 print:py-0.5 text-right uppercase text-xs print:text-[9px] tracking-wider text-ink-500">Totais</td>
                  {CATS.map((c) => {
                    const tot = (ship.itens_calc || []).reduce((a, it) => a + ((it[c.k] || {}).quantidade || 0), 0);
                    return <td key={c.k} className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right">{fmtNum(tot, 0)}</td>;
                  })}
                  <td className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right">{fmtNum(ship.total_caixas, 0)}</td>
                  <td className="px-3 py-2 print:px-1.5 print:py-0.5 font-mono text-right">{fmtBRL(ship.valor_bruto)}</td>
                </tr>
              </tfoot>
            </table>

            {/* Detalhamento de divisões (splits) — quando há preços diferenciados na mesma categoria */}
            {ship.precos_splits && Object.keys(ship.precos_splits).length > 0 && (
              <div className="mt-3 border-t border-ink-200 pt-3" data-testid="romaneio-splits-detail">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                  Detalhamento por sub-lote (preços diferenciados)
                </div>
                <table className="w-full text-xs print:text-[9px]">
                  <thead className="bg-stone-50">
                    <tr className="text-[10px] print:text-[8px] font-bold uppercase tracking-wider text-ink-500">
                      <th className="px-2 py-1 text-left">Categoria / Tipo</th>
                      <th className="px-2 py-1 text-left">Descrição</th>
                      <th className="px-2 py-1 text-right">Qtd (sc)</th>
                      <th className="px-2 py-1 text-right">Preço R$/sc</th>
                      <th className="px-2 py-1 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(ship.precos_splits).flatMap(([cat, tipoMap]) =>
                      Object.entries(tipoMap || {}).flatMap(([tipo, arr]) =>
                        (arr || []).map((s, idx) => {
                          const catLabel = (CATS.find((c) => c.k === cat)?.label) || cat.toUpperCase();
                          const q = Number(s.quantidade) || 0;
                          const p = Number(s.preco) || 0;
                          return (
                            <tr key={`${cat}-${tipo}-${idx}`} className="border-b border-ink-200 last:border-0">
                              <td className="px-2 py-1">
                                <span className="font-semibold">{catLabel}</span>{" "}
                                <span className="capitalize text-ink-500">{tipo}</span>
                              </td>
                              <td className="px-2 py-1">{s.descricao || <em className="text-ink-400">—</em>}</td>
                              <td className="px-2 py-1 font-mono text-right">{fmtNum(q, 0)}</td>
                              <td className="px-2 py-1 font-mono text-right">{fmtBRL(p)}</td>
                              <td className="px-2 py-1 font-mono text-right font-semibold">{fmtBRL(q * p)}</td>
                            </tr>
                          );
                        })
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Resumo */}
        <div className="space-y-4 print:mt-2 print:space-y-2">
          <Card className="p-6 print:p-2 print:border print:shadow-none">
            <h3 className="font-heading text-lg print:text-[11px] font-bold text-ink-900 mb-4 print:mb-1 uppercase print:tracking-widest">Resumo financeiro</h3>
            <div className="space-y-1.5 print:space-y-0 text-sm print:text-[10px]">
              <Line label="Total de caixas" value={fmtNum(ship.total_caixas, 0)} />
              <Line label="Peso aprox." value={fmtKg((ship.total_caixas || 0) * 20)} />
              <hr className="border-ink-200 my-2 print:my-1" />
              <Line label="Valor bruto" value={fmtBRL(ship.valor_bruto)} />
              <Line
                label={`${ship.desconto_op === "add" ? "Acréscimo" : "Desconto"} (${ship.desconto_pct || 0}%)`}
                value={`${ship.desconto_op === "add" ? "+ " : "- "}${fmtBRL(ship.desconto_valor)}`}
                tone={ship.desconto_op === "add" ? "default" : "warn"}
              />
              {(ship.valor_frete || 0) > 0 && (
                ship.frete_tipo === "CIF" ? (
                  <Line
                    label={`Frete CIF${ship.frete_op === "add" ? " (+)" : ""}`}
                    value={`${ship.frete_op === "add" ? "+ " : "- "}${fmtBRL(ship.valor_frete)}`}
                    tone={ship.frete_op === "add" ? "default" : "warn"}
                  />
                ) : (
                  <Line label="Frete FOB (pago pelo cliente)" value={fmtBRL(ship.valor_frete)} />
                )
              )}
              {(ship.descarga || 0) > 0 && (() => {
                const perSc = ship.descarga_mode === "saco";
                const total = perSc ? (ship.descarga || 0) * (ship.total_caixas || 0) : (ship.descarga || 0);
                return (
                  <Line
                    label={`Descarga${ship.descarga_op === "add" ? " (+)" : ""}${perSc ? ` — ${fmtBRL(ship.descarga)}/sc` : ""}`}
                    value={`${ship.descarga_op === "add" ? "+ " : "- "}${fmtBRL(total)}`}
                  />
                );
              })()}
              {(ship.seguro || 0) > 0 && (
                <Line
                  label={`Seguro${ship.seguro_op === "add" ? " (+)" : ""}`}
                  value={`${ship.seguro_op === "add" ? "+ " : "- "}${fmtBRL(ship.seguro)}`}
                />
              )}
              {(ship.comissao_valor || 0) > 0 && (
                <Line label={`Comissão${ship.vendedor ? ` — ${ship.vendedor}` : ""} (${ship.comissao_pct || 0}%)`} value={`- ${fmtBRL(ship.comissao_valor)}`} tone="warn" />
              )}
              <hr className="border-ink-200 my-2 print:my-1" />
              <div className="flex justify-between items-baseline pt-1 print:pt-0">
                <span className="text-xs print:text-[9px] font-bold uppercase tracking-[0.1em] text-ink-500">Líquido</span>
                <span className="font-mono text-2xl print:text-base font-bold text-brand-600" data-testid="shipment-detail-liquido">{fmtBRL(ship.valor_liquido)}</span>
              </div>
              {ship.data_vencimento && (
                <div className="text-xs print:text-[9px] text-ink-500 mt-2 pt-2 print:mt-1 print:pt-1 border-t border-ink-200">Vencimento: <span className="font-mono text-ink-900">{fmtDate(ship.data_vencimento)}</span></div>
              )}
            </div>
          </Card>

          {ship.observacoes && (
            <Card className="p-6 print:p-2 print:border print:shadow-none">
              <h3 className="font-heading text-sm print:text-[10px] font-bold text-ink-900 mb-2 print:mb-0.5 uppercase tracking-wider">Observações</h3>
              <p className="text-sm print:text-[10px] text-ink-700 whitespace-pre-wrap">{ship.observacoes}</p>
            </Card>
          )}

          {/* Assinaturas - print only */}
          <div className="hidden print:block mt-6">
            <div className="grid grid-cols-2 gap-8 text-center text-[9px]">
              <div>
                <div className="border-t border-stone-900 pt-1">Assinatura — Cebolas Serenata</div>
              </div>
              <div>
                <div className="border-t border-stone-900 pt-1">Assinatura — {ship.cliente}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 8mm; }
          body { margin: 0; }
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </Layout>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs print:text-[8px] uppercase tracking-wider text-ink-500 mb-0.5 print:mb-0 print:leading-tight">{label}</div>
      <div className={`text-ink-900 print:leading-tight ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Line({ label, value, tone = "default" }) {
  const t = { default: "text-ink-900", warn: "text-warn" };
  return (
    <div className="flex justify-between items-baseline print:leading-tight">
      <span className="text-ink-500">{label}</span>
      <span className={`font-mono font-medium ${t[tone]}`}>{value}</span>
    </div>
  );
}
